import { Divider } from 'semantic-ui-react';
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Typography from "@mui/material/Typography";

import ProgressBar from 'app/components/ProgressBar';
import classes from 'app/components/Modal/classes.module.scss';
import { STATUS } from 'apps/portal/utils/Constants';

const boxStyle = {
  maxWidth: 720,
  width: '90vw'
}

const getProgress = qrCodeRequest => {
  if (qrCodeRequest.progress) {
    return qrCodeRequest.progress;
  }

  let total = qrCodeRequest.total || 0;
  let count = qrCodeRequest.count || 0;
  let progress = total > 0 ? Math.floor((count / total) * 100) : 0;

  return {
    count,
    total,
    progress,
    status: qrCodeRequest.status,
    phases: []
  }
}

const formatAge = seconds => {
  if (!seconds) {
    return null;
  }

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ago`;
  }

  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
}

const formatUpdatedAt = value => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

const PhaseProgress = props => {
  const {
    phase
  } = props;

  const label = `${phase.progress}%`;
  const status = STATUS[phase.status] || 'Unknown';
  const age = formatAge(phase.stale_seconds);
  const updatedAt = formatUpdatedAt(phase.updated_at);

  return (
    <Box sx={{ margin: '1rem 0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
        <Typography variant="subtitle1">{phase.label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {status}: {phase.count} / {phase.total}
        </Typography>
      </Box>
      {(updatedAt || phase.stale) && (
        <Typography variant="body2" sx={{ color: phase.stale ? 'warning.main' : 'text.secondary' }}>
          {phase.stale ? 'Possibly stuck' : 'Last update'}{age ? ` ${age}` : ''}{updatedAt ? ` (${updatedAt})` : ''}
        </Typography>
      )}
      {phase.error && (
        <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
          Error: {phase.error}
        </Typography>
      )}
      <ProgressBar label={label} progress={phase.progress} />
    </Box>
  );
}

const QRCodeGeneratorModal = props => {
  const {
    qrCodeRequest,
    onClose
  } = props;

  const progress = getProgress(qrCodeRequest);
  const status = STATUS[progress.status] || 'Unknown';
  const age = formatAge(progress.stale_seconds);
  const updatedAt = formatUpdatedAt(progress.updated_at);

  return (
    <Modal open={true} onClose={onClose} className={classes.modal}>
      <Box className={classes.paper} style={boxStyle}>
        <CloseIcon className={classes.closeIcon} onClick={onClose} />
        <Typography variant="h5">QR Code Progress</Typography>
        <Divider />
        <Box sx={{ margin: '1rem 0' }}>
          <Typography variant="subtitle1">Overall</Typography>
          <Typography variant="body2" color="text.secondary">
            {status}: {progress.count} / {progress.total}
          </Typography>
          {(updatedAt || progress.stale) && (
            <Typography variant="body2" sx={{ color: progress.stale ? 'warning.main' : 'text.secondary' }}>
              {progress.stale ? 'Possibly stuck' : 'Last update'}{age ? ` ${age}` : ''}{updatedAt ? ` (${updatedAt})` : ''}
            </Typography>
          )}
          {progress.error && (
            <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
              Error: {progress.error}
            </Typography>
          )}
          <ProgressBar label={`${progress.progress}%`} progress={progress.progress} />
        </Box>
        <Divider />
        {progress.phases.map(phase => (
          <PhaseProgress key={phase.phase} phase={phase} />
        ))}
        <Divider />
        <div>
          <Button className={classes.button} variant="contained" color="primary" onClick={onClose}>Close</Button>
        </div>
      </Box>
    </Modal>
  );
}

export default QRCodeGeneratorModal;
